import { Layout } from '../../queries/ats/layout/types';
import { ThirdPartyProvider } from '../../queries/ats/me/providers/types';
import { ThirdPartySource } from '../../queries/ats/titles/types/sources';
import { Title } from '../../queries/ats/titles/types/title';
import { invariant } from '../../utils/commonUtils';
import { TrackerEvent } from '../interfaces/tracker-event';

interface ThirdPartyServiceOpenedBaseProperties {
  open_method: 'Details panel' | 'Layout item link' | 'App swimlane/grid';
  content_title: string;
  referral_id: string;
  service_name: string;
  service_type: 'thirdParty';
  page_slug?: string;
  section_slug?: string;
}

interface ThirdPartyServiceOpenedLinkProperties extends ThirdPartyServiceOpenedBaseProperties {
  open_method: 'Layout item link' | 'App swimlane/grid';
  content_title: string;
  referral_id: string;
  service_name: string;
  service_type: 'thirdParty';
  page_slug: string;
  section_slug: string;
}

export interface ThirdPartyServiceOpenedTitleProperties extends ThirdPartyServiceOpenedBaseProperties {
  open_method: 'Details panel';
  master_id: string;
  content_title: string;
  content_type: Title['type'];
  genres: string[];
  episode_number: number | undefined;
  season_number: number | undefined;
  series_id: string | undefined;
  provider_title_id: string;
  service_name: string;
  service_type: 'thirdParty';
  referral_id: string;
}

type ThirdPartyServiceOpenedProperties = ThirdPartyServiceOpenedLinkProperties | ThirdPartyServiceOpenedTitleProperties;

export class ThirdPartyServiceOpenedEvent implements TrackerEvent<ThirdPartyServiceOpenedProperties> {
  public name = 'thirdparty_service_opened';
  public properties: ThirdPartyServiceOpenedProperties;

  constructor(
    item: Title | Layout.SectionItemLink,
    provider: ThirdPartySource | ThirdPartyProvider,
    pageSlug: string | undefined,
    sectionSlug: string | undefined,
    pattern?: string,
  ) {
    if (item.type === 'link') {
      invariant(pageSlug !== undefined, 'Page must be defined when tracking a section item of type "link".');
      invariant(sectionSlug !== undefined, 'Section must be defined when tracking a section item of type "link".');
      invariant(pattern !== undefined, 'Pattern must be defined when tracking a section item of type "link".');
      this.properties = this.linkProperties(item, provider, pageSlug, sectionSlug, pattern);
    } else {
      this.properties = this.titleProperties(item, provider, pageSlug, sectionSlug);
    }
  }

  private linkProperties(
    item: Layout.SectionItemLink,
    provider: ThirdPartySource | ThirdPartyProvider,
    pageSlug: string,
    sectionSlug: string,
    pattern: string,
  ): ThirdPartyServiceOpenedLinkProperties {
    const openMethod = pattern === 'streaming-service' ? 'App swimlane/grid' : 'Layout item link';
    return {
      open_method: openMethod,
      content_title: item.params.linkTitle,
      service_name: provider.name,
      service_type: 'thirdParty',
      referral_id: sectionSlug,
      page_slug: pageSlug,
      section_slug: sectionSlug,
    };
  }

  private titleProperties(
    item: Title,
    provider: ThirdPartySource | ThirdPartyProvider,
    pageSlug: string | undefined,
    sectionSlug: string | undefined,
  ): ThirdPartyServiceOpenedTitleProperties {
    return {
      open_method: 'Details panel',
      master_id: item.masterId,
      content_title: item.originalTitle,
      content_type: item.type,
      genres: item.genres,
      episode_number: item.type === 'episode' ? item.episode.sequenceNumber : undefined,
      season_number: item.type === 'episode' ? item.season.sequenceNumber : undefined,
      series_id: item.type === 'series' || item.type === 'episode' ? item.series.masterId : undefined,
      provider_title_id: provider.id,
      service_name: provider.name,
      service_type: 'thirdParty',
      referral_id: item.type === 'series' || item.type === 'episode' ? item.series.masterId : item.masterId,
      page_slug: pageSlug,
      section_slug: sectionSlug,
    };
  }
}
